import { ICookieConsentModalProps } from "../types/modal";

// utils
import { useMatomo } from "@jonkoops/matomo-tracker-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../hooks/redux";
import { closeModalById } from "../features/modals";

// components
import { Link as RouterLink } from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Link,
    Stack,
    Divider,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { LanguageNavbarItem, SettingsNavbarItem } from "../parts/NavMenu";
import USED_COOKIES from "../constants/usedCookies";
import { Translations } from "../localization/translations";

const CookieConsent: React.FunctionComponent<ICookieConsentModalProps> = (
    props
) => {
    const { pushInstruction } = useMatomo();
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();

    const onFullConsent = () => {
        pushInstruction("rememberConsentGiven");
        pushInstruction("rememberCookieConsentGiven");
        pushInstruction("setCookieConsentGiven");
        pushInstruction("setConsentGiven");
        dispatch(closeModalById(props.id));

        window.location.reload();
    };

    const onLimitedConsent = () => {
        pushInstruction("forgetConsentGiven");
        pushInstruction("forgetCookieConsentGiven");
        dispatch(closeModalById(props.id));

        window.location.reload();
    };

    const closeModal = () => {
        dispatch(closeModalById(props.id));
    };

    return (
        <Dialog open={true} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h5">
                        {t(Translations.cookies.modal.title)}
                    </Typography>
                    <Stack direction="row">
                        <Divider orientation="vertical" flexItem />
                        <LanguageNavbarItem />
                        <Divider orientation="vertical" flexItem />
                        <SettingsNavbarItem minimal />
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Divider flexItem />
                    <Typography>
                        {t(Translations.cookies.text.caption1)}
                    </Typography>
                    <Typography>
                        {t(Translations.cookies.text.caption2)}
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="space-between"
                    >
                        <Button
                            variant="contained"
                            size="large"
                            onClick={onFullConsent}
                        >
                            {t(Translations.cookies.button.fullConsent)}
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={onLimitedConsent}
                        >
                            {t(Translations.cookies.button.limitedConsent)}
                        </Button>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Link
                            to={`/${i18n.language}/static-page/privacy`}
                            underline="always"
                            style={{ textDecoration: "underline" }}
                            component={RouterLink}
                            onClick={closeModal}
                        >
                            {t(Translations.cookies.links.privacyPolicy)}
                        </Link>
                        <Link
                            to={`/${i18n.language}/static-page/accessibility`}
                            color="inherit"
                            style={{ textDecoration: "underline" }}
                            component={RouterLink}
                            onClick={closeModal}
                        >
                            {t(Translations.cookies.links.accessibility)}
                        </Link>
                    </Stack>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>
                                {t(
                                    Translations.cookies.button.aboutUsedCookies
                                )}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails style={{ overflowY: "auto" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            {t(
                                                Translations.cookies.column
                                                    .cookie
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                Translations.cookies.column
                                                    .group
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                Translations.cookies.column
                                                    .description
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                Translations.cookies.column
                                                    .expirationDate
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {USED_COOKIES.map((usedCookie) => (
                                        <TableRow key={usedCookie.cookie}>
                                            <TableCell>
                                                {usedCookie.cookie}
                                            </TableCell>
                                            <TableCell>
                                                {usedCookie.group}
                                            </TableCell>
                                            <TableCell>
                                                {usedCookie.description}
                                            </TableCell>
                                            <TableCell>
                                                {usedCookie.expiration}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default CookieConsent;
